import React from 'react';
import { Box, Typography, Card, CardContent, Grid, Chip, IconButton, Tooltip } from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import {
  COLOR_TEXT_GREY,
  COLOR_TEXT_4,
  COLOR_TEXT_2,
  COLOR_PRIMARY_2,
  COLOR_GREY_200,
} from 'src/utils/colors';
import { convertCurrency, formatCurrency, formatMarketName } from 'src/utils/functions';
import { useSelector } from 'react-redux';
import { exchangeRates } from 'src/utils/const';
import TooltipNew from 'src/App/Dashboards/Market/Components/Tooltips/TooltipNew';
import SubmarketMarketGrade from './SubmarketMarketGrade';
import { Search } from 'src/interfaces/idashboard';
// Global variable to control blur and lock visibility

const MetricBox = ({
  label,
  value,
  isFullWidth = false,
  currency,
  isDisabled,
  handleOnSampleClick,
}: {
  label: string;
  value: string | number;
  currency: string | null;
  isDisabled: boolean;
  isFullWidth?: boolean;
  handleOnSampleClick: (plan: string) => void;
}) => (
  <Box
    sx={{
      textAlign: 'left',
      position: 'relative',
      width: isFullWidth ? '100%' : 'auto',
      maxHeight: '50px',
    }}
  >
    <Typography variant="body1" sx={{ color: COLOR_TEXT_4, mb: 0.5, fontSize: '12px' }}>
      {label}
    </Typography>
    <Typography
      variant="body1"
      sx={{
        fontWeight: 600,
        color: COLOR_TEXT_2,
        filter: isDisabled ? 'blur(4px)' : 'none',
      }}
    >
      {currency && exchangeRates[currency].symbol}
      {value}
    </Typography>
    {isDisabled && (
      <IconButton
        onClick={() => handleOnSampleClick('expert')}
        size="small"
        sx={{ transform: 'translateY(-20px)', p: 0, m: 0 }}
      >
        <LockIcon sx={{ color: COLOR_PRIMARY_2 }} />
      </IconButton>
    )}
  </Box>
);

const SubmarketCard = ({
  submarketData,
  isSubmarketAccessAllowed,
  handleOnSampleClick,
}: {
  submarketData: any;
  isSubmarketAccessAllowed: boolean;
  handleOnSampleClick: (plan: string) => void;
}) => {
  const currency = useSelector((state: any) => state.dashboard.currency);
  const convertAndFormatCurrency = (val: number) =>
    formatCurrency(String(convertCurrency(currency || 'USD', val)));

  return (
    <Card
      sx={{
        width: '100%',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
        borderRadius: '12px',
        overflow: 'hidden',
        border: '1px solid #f0f0f0',
        mb: 1,
      }}
    >
      <CardContent sx={{ p: '0px !important' }}>
        {/* Header with rank and submarket name */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <Typography
            variant="h5"
            sx={{
              fontSize: '20px',
              fontWeight: 600,
              color: COLOR_TEXT_2,
              flex: 1,
              display: 'flex',
              alignItems: 'flex-start',
              flexDirection: 'row',
            }}
          >
            <SubmarketMarketGrade
              neighborhoodGrade={submarketData.neighborhood_grade}
              isSubmarketAccessAllowed={isSubmarketAccessAllowed}
              handleOnSampleClick={handleOnSampleClick}
              verifiedTier={submarketData.verified_tier}
            />
            {formatMarketName({ name: submarketData.submarket_name } as Search)}
          </Typography>
          {submarketData.rank && (
            <Chip
              label={`#${submarketData.rank}`}
              size="medium"
              sx={{
                backgroundColor: '#f5f5f5',
                color: '#666',
                fontSize: '14px',
                fontWeight: 600,
                height: '32px',
                minWidth: '48px',
                '& .MuiChip-label': {
                  px: 1.5,
                },
              }}
            />
          )}
        </Box>

        {/* Metrics Row */}
        <Grid container spacing={3}>
          {/* All metrics in one row */}
          <Grid item xs={3}>
            <MetricBox
              label="Supply"
              value={formatCurrency(String(submarketData.supply))}
              currency={null}
              isDisabled={false}
              handleOnSampleClick={handleOnSampleClick}
            />
          </Grid>
          <Grid item xs={3}>
            <MetricBox
              label="Daily Rate"
              value={convertAndFormatCurrency(submarketData.adr)}
              currency={currency}
              isDisabled={!isSubmarketAccessAllowed}
              handleOnSampleClick={handleOnSampleClick}
            />
          </Grid>
          <Grid item xs={3}>
            <MetricBox
              label="Occupancy"
              value={`${submarketData.occupancy_rate}%`}
              currency={null}
              isDisabled={!isSubmarketAccessAllowed}
              handleOnSampleClick={handleOnSampleClick}
            />
          </Grid>
          <Grid item xs={3}>
            <MetricBox
              label="Revenue"
              value={convertAndFormatCurrency(submarketData.revenue)}
              currency={currency}
              isDisabled={!isSubmarketAccessAllowed}
              handleOnSampleClick={handleOnSampleClick}
            />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

export default SubmarketCard;
